import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAlertEvents,
  fetchCorrections,
  fetchMembers,
  fetchTaskSummary,
  fetchTasks,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { dapatMemutuskanKoreksi, dapatMengelolaTugas } from '../lib/bel-pemberitahuan';
import { formatCorrectionType } from '../lib/correction-form';
import { formatDateInZone } from '../lib/format';
import { barisPerhatianLain, syaratBarisLain } from '../lib/perlu-perhatian';
import { ambangAwal, tugasSelesaiSejak } from '../lib/pemberitahuan';
import { pengajuKoreksi, petaNamaAnggota } from '../lib/pengaju-koreksi';
import { bacaTerakhirDilihat } from '../lib/terakhir-dilihat';

/** Sebanyak ini yang muat di beranda tanpa mendorong tombol absen keluar layar. */
const MAKS_BARIS = 3;

/**
 * "Perlu perhatian Anda" — pemberitahuan yang muncul di BERANDA, bukan hanya di balik lonceng.
 *
 * ADA KARENA LENCANA SAJA TIDAK MEMBERITAHU APA PUN. Saat Jihan mengajukan izin sakit, lonceng
 * menyala dan berhenti di situ: manajer harus menebak ada apa, lalu menekannya untuk mencari
 * tahu. Yang dibaca lebih dulu setiap pagi adalah beranda, dan pengajuan yang menunggu keputusan
 * pantas terlihat di sana.
 *
 * TIDAK MENANDAI "SUDAH DILIHAT". Itu tugas halaman pemberitahuan. Kalau beranda ikut menandai,
 * lencana loncengnya padam hanya karena seseorang membuka aplikasi — pemberitahuan hilang tanpa
 * pernah dibaca siapa pun, dan itu arah gagal yang paling berbahaya karena menenangkan.
 *
 * DIAM SAAT TIDAK ADA APA-APA. Tidak ada kartu kosong bertuliskan "tidak ada pemberitahuan":
 * beranda ini dibuka di ponsel sambil berdiri, dan setiap kartu yang tidak membawa keterangan
 * mendorong tombol absen — satu-satunya alasan kurir membuka layar ini — turun menjauh dari jempol.
 *
 * GAGAL DENGAN DIAM. Kegagalan memuat tidak memunculkan kartu galat. Beranda harus tetap dapat
 * dipakai absen meski daftar pengajuan tidak dapat diambil; kartu merah di atas tombol absen
 * menukar gangguan kecil dengan gangguan besar.
 */
export function PerluPerhatian(): ReactNode {
  const session = useSession();
  const bolehKoreksi = dapatMemutuskanKoreksi(session.me.permissions);
  const bolehTugas = dapatMengelolaTugas(session.me.permissions);

  /*
   * Hook dipanggil tanpa syarat — memanggilnya di balik `if` melanggar aturan hook. Yang
   * disyaratkan isinya: tanpa izin, tidak ada permintaan yang ditembakkan sama sekali.
   */
  const koreksi = useAsync(
    () => (bolehKoreksi ? fetchCorrections(session.auth, 'PENDING') : Promise.resolve([])),
    [session.auth, bolehKoreksi],
  );
  const tugas = useAsync(
    () => (bolehTugas ? fetchTasks(session.auth, 'completed') : Promise.resolve([])),
    [session.auth, bolehTugas],
  );
  const anggota = useAsync(
    () => (bolehKoreksi || bolehTugas ? fetchMembers(session.auth) : Promise.resolve([])),
    [session.auth, bolehKoreksi, bolehTugas],
  );

  /*
   * Draf & alert terbuka (#1758) — HANYA pengawas; aturan & alasannya di `syaratBarisLain`.
   */
  const syarat = syaratBarisLain(session.me.roles, session.me.permissions);
  const bolehDraf = syarat.draf;
  const bolehAlert = syarat.alert;
  const ringkasTugas = useAsync(
    () => (bolehDraf ? fetchTaskSummary(session.auth) : Promise.resolve(null)),
    [session.auth, bolehDraf],
  );
  const alert = useAsync(
    () => (bolehAlert ? fetchAlertEvents(session.auth, { open: true }) : Promise.resolve(null)),
    [session.auth, bolehAlert],
  );
  const barisLain = barisPerhatianLain({
    tugasDraf:
      ringkasTugas.state.status === 'ready' && ringkasTugas.state.data
        ? ringkasTugas.state.data.draft
        : null,
    alertTerbuka:
      alert.state.status === 'ready' && alert.state.data ? alert.state.data.length : null,
  });

  const petaNama = useMemo(
    () => petaNamaAnggota(anggota.state.status === 'ready' ? anggota.state.data : []),
    [anggota.state],
  );

  const baruSelesai = useMemo(() => {
    if (tugas.state.status !== 'ready') return [];
    const sejak = bacaTerakhirDilihat() ?? ambangAwal(new Date());
    return tugasSelesaiSejak(tugas.state.data, sejak);
  }, [tugas.state]);

  const menunggu = koreksi.state.status === 'ready' ? koreksi.state.data : [];
  if (menunggu.length === 0 && baruSelesai.length === 0 && barisLain.length === 0) return null;

  return (
    <section className="card perlu-perhatian">
      <div className="row space-between">
        <h3>Perlu perhatian Anda</h3>
        <Link to="/pemberitahuan">Lihat semua</Link>
      </div>

      {menunggu.length > 0 ? (
        <ul className="list ringkas">
          {menunggu.slice(0, MAKS_BARIS).map((k) => (
            <li key={k.id}>
              {/*
                Nama lebih dulu, lalu APA yang diajukan. "Menunggu keputusan" tanpa nama dan tanpa
                jenisnya menuntut manajer membuka halaman lain hanya untuk tahu ia perlu peduli
                atau tidak — dan pemberitahuan yang menuntut penyelidikan akan berhenti dibaca.
              */}
              <Link to="/attendance/corrections">
                <strong>{pengajuKoreksi(petaNama, k).subjek}</strong> ·{' '}
                {formatCorrectionType(k.correctionType)}
              </Link>
              <span className="muted small">
                {' '}
                — {formatDateInZone(`${k.workDate}T00:00:00.000Z`, 'UTC')}
              </span>
            </li>
          ))}
          {menunggu.length > MAKS_BARIS ? (
            <li className="muted small">dan {menunggu.length - MAKS_BARIS} pengajuan lain</li>
          ) : null}
        </ul>
      ) : null}

      {baruSelesai.length > 0 ? (
        <p className="muted small">
          <Link to="/pemberitahuan">{baruSelesai.length} tugas baru diselesaikan kurir</Link>
        </p>
      ) : null}

      {barisLain.map((b) => (
        <p key={b.kunci} className="muted small">
          <Link to={b.to}>{b.teks}</Link>
        </p>
      ))}
    </section>
  );
}
